import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { stripeConfigured } from '@/lib/stripe';
import { useCart, cartTotal } from '@/store/cart';
import { deliveryFee as deliveryFeeFor, maxTier, type ParcelTier } from '@/lib/delivery';
import { useAuthStore } from '@/store/auth';
import { GuestCheckoutModal, type GuestCheckoutPayload } from '@/components/order/GuestCheckoutModal';
import { LockerMobilePrompt } from '@/components/order/LockerMobilePrompt';

export default function Cart() {
  const { items, remove, delivery: savedDelivery, setDelivery: saveDelivery } = useCart();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [loading, setLoading] = useState(false);
  const [showGuest, setShowGuest] = useState(false);
  const [needMobile, setNeedMobile] = useState(false);
  // Delivery already picked on the product page? Use it and don't ask again -
  // the buyer can still change it via "Change".
  const [delivery, setDelivery] = useState<'locker' | 'standard' | 'express'>(savedDelivery ?? 'locker');
  const [editDelivery, setEditDelivery] = useState(!savedDelivery);
  const chooseDelivery = (v: 'locker' | 'standard' | 'express') => { setDelivery(v); saveDelivery(v); };

  const { data: membership } = useQuery<{
    tier: string; shippingCreditGBP?: number;
    voucherRedeemable?: boolean;
    voucherExpiresAt?: string | null;
    spendToNextVoucherGBP?: number;
    usage?: { ordersLeft: number; spendLeftGBP: number; feeFree: boolean };
  }>({
    queryKey: ['membership'],
    queryFn: async () => (await api.get('/membership')).data,
    enabled: !!user,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const isMember = membership?.tier === 'MEMBER';
  const [useCredit, setUseCredit] = useState(false);
  // Souqli Credit = refunds held on the account, spendable here.
  const [useSouqliCredit, setUseSouqliCredit] = useState(true);
  const { data: wallet } = useQuery<{ buyer?: { creditGBP: number } }>({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get('/me/wallet')).data,
    enabled: !!user, // guests have no wallet/Souqli credit - and a 401 here would wipe the bag
    retry: false,
  });

  const subtotal = cartTotal(items);
  // Delivery is priced by the LARGEST parcel tier in the bag (a big item can't
  // ride a small item's rate); the buyer only chooses the carrier.
  const cartTier = items.reduce<ParcelTier>((t, i) => maxTier(t, i.parcelTier ?? 'MEDIUM'), 'SMALL');
  const DELIVERY = {
    locker:   { label: 'InPost Locker',      carrier: 'InPost',     sub: 'Locker pickup · 2-4 days',   icon: '📦', fee: deliveryFeeFor('locker', cartTier) },
    standard: { label: 'Royal Mail Tracked', carrier: 'Royal Mail', sub: 'Tracked to door · 3-5 days', icon: '📮', fee: deliveryFeeFor('standard', cartTier) },
    express:  { label: 'DPD Express',        carrier: 'DPD',        sub: 'Express courier · 1-2 days', icon: '🚀', fee: deliveryFeeFor('express', cartTier) },
  } as const;
  // One delivery method for the order - charged once, never stacked per item.
  const grossDelivery = DELIVERY[delivery].fee;
  /*
   * £4 voucher.
   *
   * The redeem option appears only when the member actually holds an unearned,
   * unexpired £4 balance - the server decides that (`voucherRedeemable`) rather
   * than the client re-deriving the rule from a number, so the two can't drift.
   * A voucher is earned once a member's spend passes £300 WITHIN a calendar
   * month; spend does not carry over from previous months.
   */
  const canUseCredit = isMember && membership?.voucherRedeemable === true;
  const spendToVoucher = membership?.spendToNextVoucherGBP ?? 0;
  const shippingCredit = canUseCredit && useCredit ? Math.min(4, grossDelivery) : 0;
  const deliveryFee = grossDelivery - shippingCredit;
  const creditAvailable = wallet?.buyer?.creditGBP ?? 0;
  // Fee estimate mirrors the server: guests 4.5% + 70p per order; members free
  // inside the monthly allowance (12 orders / £300), then 3.5% + 60p per order.
  const usage = membership?.usage;
  const withinAllowance = isMember && (usage
    ? usage.ordersLeft > 0 && subtotal <= usage.spendLeftGBP
    : true);
  const feeRate = !isMember ? 0.045 : (withinAllowance ? 0 : 0.035);
  const flatPerItem = !isMember ? 0.70 : (withinAllowance ? 0 : 0.60);
  const serviceFee = subtotal * feeRate + items.length * flatPerItem;
  const vat = 0; // VAT removed
  const estTotal = subtotal + serviceFee + deliveryFee + vat;
  // Credit is capped at the order total - it reduces the card charge, it never
  // pays out through checkout.
  const creditApplied = useSouqliCredit ? Math.min(creditAvailable, estTotal) : 0;
  const payNow = estTotal - creditApplied;

  async function checkout(guestInfo?: GuestCheckoutPayload) {
    if (!user && !guestInfo) { setShowGuest(true); return; }
    if (!items.length) return;
    setLoading(true);
    try {
      const snapshots = Object.fromEntries(items.map((i) => [i.id, { title: i.title, brand: i.brand, priceGBP: i.priceGBP, imageUrl: i.imageUrl }]));
      const { data } = await api.post('/orders/cart', { productIds: items.map((i) => i.id), deliveryService: delivery, preferDemo: !stripeConfigured, useShippingCredit: useCredit && canUseCredit, useSouqliCredit, snapshots, ...(guestInfo ? { guest: guestInfo.guest, shippingAddress: guestInfo.shippingAddress } : {}) });
      if (Array.isArray(data.unavailableIds) && data.unavailableIds.length) {
        data.unavailableIds.forEach((id: string) => remove(id));
        toast('Some items were no longer available and were removed from your bag.', { icon: '⚠️' });
      }
      setShowGuest(false);
      navigate('/checkout/cart', {
        state: {
          clientSecret: data.clientSecret,
          guest: data.guest,
          demo: data.demo,
          paymentRef: data.paymentRef,
          amountGBP: data.totalGBP,
          title: `${data.itemCount} item${data.itemCount > 1 ? 's' : ''} from your bag`,
          reference: data.reference,
          cart: true,
          breakdown: data.breakdown,
        },
      });
    } catch (e: any) {
      if (e?.response?.data?.code === 'LOCKER_NEEDS_MOBILE' && user) { setNeedMobile(true); }
      else toast.error(e?.response?.data?.message ?? 'Could not start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!items.length) {
    return (
      <div className="bg-cream min-h-[70vh]">
        <div className="max-w-[680px] mx-auto px-6 py-20 text-center">
          <div className="text-5xl mb-4">🛍️</div>
          <h1 className="display text-4xl">Your bag is empty</h1>
          <p className="text-mute text-sm mt-2">Add pieces you love and check out securely with full buyer protection.</p>
          <Link to="/c/all" className="btn btn-primary mt-6">Browse the marketplace</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-[70vh]">
      <div className="max-w-[1040px] mx-auto px-6 py-12">
        <div className="stamp text-gold-dark text-[9px]">YOUR BAG</div>
        <h1 className="display text-4xl mt-1 mb-8">{items.length} item{items.length > 1 ? 's' : ''} ready to checkout</h1>

        <div className="grid md:grid-cols-[1fr_320px] gap-8 items-start">
          {/* Items */}
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} className="card-flat p-3 flex items-center gap-4">
                <Link to={`/p/${it.id}`} className="w-20 h-24 notched-sm bg-cover bg-center bg-cream-deep shrink-0" style={{ backgroundImage: it.imageUrl ? `url(${it.imageUrl})` : undefined }} />
                <div className="flex-1 min-w-0">
                  {it.brand && <div className="stamp text-mute text-[9px]">{it.brand.toUpperCase()}</div>}
                  <Link to={`/p/${it.id}`} className="text-sm font-medium hover:text-gold-dark block truncate">{it.title}</Link>
                  <div className="display text-lg mt-1">£{it.priceGBP.toFixed(0)}</div>
                </div>
                <button onClick={() => remove(it.id)} className="text-mute hover:text-bad text-sm shrink-0" aria-label="Remove">Remove</button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <aside className="card p-6 md:sticky md:top-24">
            <div className="flex items-center justify-between">
              <div className="stamp text-gold-dark text-[9px]">DELIVERY</div>
              {!editDelivery && (
                <button type="button" onClick={() => setEditDelivery(true)} className="text-[11px] text-gold-dark hover:underline">Change</button>
              )}
            </div>
            {editDelivery ? (
              <div className="mt-2 space-y-1.5">
                {(['locker', 'standard', 'express'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => chooseDelivery(v)}
                    className={`w-full flex items-center gap-2.5 p-2 rounded-luxe border text-left transition ${delivery === v ? 'border-gold bg-gold/8' : 'border-warmline hover:border-gold/50'}`}
                  >
                    <span className={`w-3.5 h-3.5 rounded-full border-2 grid place-items-center shrink-0 ${delivery === v ? 'border-gold' : 'border-warmline'}`}>
                      {delivery === v && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
                    </span>
                    <span className="text-sm shrink-0">{DELIVERY[v].icon}</span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-[12.5px] font-bold leading-none ${delivery === v ? 'text-gold-dark' : 'text-ink'}`}>{DELIVERY[v].carrier}</span>
                      <span className="block text-[10.5px] text-mute mt-0.5">{DELIVERY[v].sub}</span>
                    </span>
                    <span className="text-[12px] font-semibold shrink-0">£{DELIVERY[v].fee.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2 p-2 rounded-luxe border border-gold bg-gold/8">
                <span className="text-gold-dark shrink-0">✓</span>
                <span className="flex-1 text-[12px] font-medium">{DELIVERY[delivery].label}</span>
                <span className="text-[12px] font-semibold">£{DELIVERY[delivery].fee.toFixed(2)}</span>
              </div>
            )}
            {delivery === 'locker' && (
              <p className="mt-2 text-[10.5px] text-mute leading-snug">📦 InPost is locker collection, not to your door. Your parcel goes to a nearby InPost locker and you open it with a one-time code we text you. Add a UK mobile to your delivery details.</p>
            )}
            <div className="stamp text-gold-dark text-[9px] mt-5">ORDER SUMMARY</div>
            <div className="mt-2 space-y-2 text-sm">
              <Row k={`Items (${items.length})`} v={`£${subtotal.toFixed(2)}`} />
              <Row
                k={!isMember
                  ? 'Buyer protection fee (4.5% + 70p)'
                  : withinAllowance ? 'Buyer protection (Member · included)' : 'Buyer protection (Member · 3.5% + 60p)'}
                v={`£${serviceFee.toFixed(2)}`}
              />
              <Row k="Delivery" v={`£${grossDelivery.toFixed(2)}`} />
              {shippingCredit > 0 && <Row k="£4 delivery voucher" v={`−£${shippingCredit.toFixed(2)}`} />}
            </div>
            {canUseCredit ? (
              <label className="mt-3 flex items-start gap-2 text-[12px] text-ink-700 cursor-pointer rounded-luxe border border-gold/40 bg-gold/8 px-3 py-2">
                <input type="checkbox" className="mt-0.5 accent-gold" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} />
                <span>
                  Redeem my <span className="font-semibold text-gold-dark">£4 delivery voucher</span> on this order.
                  {membership?.voucherExpiresAt && (
                    <span className="text-mute"> Expires {new Date(membership.voucherExpiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}.</span>
                  )}
                </span>
              </label>
            ) : isMember && spendToVoucher > 0 ? (
              // No voucher yet - say what unlocks one instead of offering a
              // checkbox that would do nothing.
              <p className="mt-3 text-[12px] text-mute rounded-luxe border border-warmline px-3 py-2 leading-relaxed">
                Spend £{spendToVoucher.toFixed(2)} more this month to earn a £4 delivery voucher.
                The £300 target resets on the 1st.
              </p>
            ) : null}
            {/* Souqli Credit - a refund the buyer already has, applied before
                the card is touched so they are never charged for money they
                already hold with us. */}
            {creditAvailable > 0 && (
              <label className="mt-3 flex items-start gap-2 text-[12px] text-ink-700 cursor-pointer rounded-luxe border border-sage/40 bg-sage/8 px-3 py-2">
                <input type="checkbox" className="mt-0.5 accent-gold" checked={useSouqliCredit} onChange={(e) => setUseSouqliCredit(e.target.checked)} />
                <span>
                  Use my <span className="font-semibold text-sage-dark">£{creditAvailable.toFixed(2)} Souqli Credit</span>
                  {creditApplied < creditAvailable
                    ? <> - £{creditApplied.toFixed(2)} covers this order, £{(creditAvailable - creditApplied).toFixed(2)} stays on your account.</>
                    : <> on this order.</>}
                </span>
              </label>
            )}

            <div className="border-t border-warmline mt-4 pt-4 space-y-2">
              {creditApplied > 0 && (
                <>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-mute">Order total</span>
                    <span className="text-mute">£{estTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-sage-dark">Souqli Credit</span>
                    <span className="text-sage-dark">−£{creditApplied.toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-mute">{creditApplied > 0 ? 'To pay' : 'Total'}</span>
                <span className="display text-2xl">£{payNow.toFixed(2)}</span>
              </div>
            </div>
            <button onClick={() => checkout()} disabled={loading} className="btn btn-lg btn-primary w-full mt-5">
              {loading ? 'Starting checkout…' : 'Checkout securely →'}
            </button>
            <Link to="/c/all" className="block text-center text-[12px] text-gold-dark hover:underline mt-3">Continue shopping</Link>
          </aside>
        </div>
      </div>
      <GuestCheckoutModal open={showGuest} onClose={() => setShowGuest(false)} submitting={loading} onConfirm={(p) => checkout(p)} />
      <LockerMobilePrompt open={needMobile} onClose={() => setNeedMobile(false)} onSaved={() => { setNeedMobile(false); checkout(); }} />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-mute">{k}</span>
      <span className="text-ink">{v}</span>
    </div>
  );
}
